import React from "react";
import { motion } from "framer-motion";
import { Car, Home, Check, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const BLOCKS = [
  {
    id: "auto",
    testid: "nav-block-auto",
    icon: Car,
    title: "Auto Detailing",
    subtitle: "Véhicules",
    image: IMAGES.autoCard,
    features: [
      "Nettoyage intérieur & extérieur profond",
      "Polissage & correction de peinture",
      "Protection céramique longue durée",
    ],
    cta: "Réserver un detailing",
  },
  {
    id: "habitat",
    testid: "nav-block-habitat",
    icon: Home,
    title: "Habitat & Extérieurs",
    subtitle: "Patrimoine bâti",
    image: IMAGES.habitatCard,
    features: [
      "Restauration terrasses bois / pierre",
      "Nettoyage d'allées, cours & pavés",
      "Nettoyage de façades",
      "Nettoyage de panneaux solaires",
      "Nettoyage de garages & annexes",
    ],
    cta: "Réserver une intervention",
  },
];

const CARD_HIDDEN = { opacity: 0, y: 30 };
const CARD_SHOW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "-80px" };
const CARD_HOVER = { y: -6 };

export const SplitNav = ({ onReserve, onBook }) => {
  return (
    <section className="relative overflow-hidden py-20 md:py-28 bg-background">
      <AnimatedLines />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Deux expertises, un savoir-faire
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mt-3">
            Detailing auto & nettoyage habitat à Belfort et Montbéliard
          </h2>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg">
            Chaque prestation dispose de son propre tunnel de réservation sur rendez-vous.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {BLOCKS.map((b, i) => (
            <motion.div
              key={b.id}
              id={b.id}
              data-testid={b.testid}
              initial={CARD_HIDDEN}
              whileInView={CARD_SHOW}
              viewport={CARD_VIEWPORT}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={CARD_HOVER}
              className="group relative overflow-hidden rounded-sm bg-ink min-h-[460px] flex flex-col justify-end scroll-mt-44"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${b.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/40 group-hover:from-black transition-colors duration-500" />

              <div className="relative p-8 md:p-10 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-11 h-11 grid place-items-center bg-white/15 rounded-sm">
                    <b.icon className="w-6 h-6" />
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">
                    {b.subtitle}
                  </span>
                </div>
                <h3 className="font-heading font-extrabold text-3xl tracking-tight">
                  {b.title}
                </h3>

                <ul className="mt-5 space-y-2.5">
                  {b.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                      <Check className="w-4 h-4 text-white mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    data-testid={`${b.testid}-cta`}
                    onClick={onBook}
                    className="cta inline-flex items-center gap-2 bg-royal text-white font-extrabold px-6 py-3 rounded-sm hover:bg-royal-hover shadow-md shadow-royal/30"
                  >
                    Réserver mon créneau
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`${b.testid}-quote`}
                    onClick={() => onReserve(b.id)}
                    className="cta inline-flex items-center gap-2 border border-white/40 text-white font-bold px-6 py-3 rounded-sm hover:bg-white hover:text-ink"
                  >
                    Mon devis
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
